// @refresh reload
import './app.css';
import { ErrorHandler, Navigator, StateTransfer, useUseCase } from '@magicdoor/magic-use-case-solid';
import { FailToFetchError, UnauthorizedError } from '@magicdoorinc/errors';
import { FileServiceProvider } from '@magicdoorinc/ui';
import * as Sentry from '@sentry/solid';
import { MetaProvider } from '@solidjs/meta';
import { Router, useNavigate } from '@solidjs/router';
import { FileRoutes } from '@solidjs/start/router';
import { For, onMount, Show, Suspense } from 'solid-js';
import { isServer } from 'solid-js/web';
import { CustomMeta } from '~/components/common/Meta';
import { Button } from '~/components/common/buttons/Button';
import { ImagePreview } from './components/common/ImagePreview';
import HomeLayout from './components/layouts/HomeLayout';
import {
  ApplicationNotQualifiedError,
  BusinessInfoNotFoundError,
  CannotLoadPdfFileError,
  CannotRenderPdfPageError,
  CannotMakePaymentsError,
  CoApplicantInviteFailedError,
  CannotPerformScreeningError,
  CurrentUnitUnlistedError,
  FileNotAcceptedError,
  FileSessionResponseInvalidError,
  FileUploadFailedError,
  InsuranceLinkNotFoundError,
  LandlordVerificationResponseRequiredError,
  LeaseInsuranceMissingFieldsError,
  LeaseOrSignatureNotFoundError,
  PaymentDeclinedError,
  RentalApplicationNotFoundError,
  RentalApplicationPaymentFailedError,
  StripeUnavailableError,
  ValidationError,
} from './errors/errors';
import { fileGateway } from './gateways/fileGateway';
import { CompanyStyling } from './global-contexts/CompanyStyling';
import { TranslationKeys, useLocalization } from './hooks/useLocalization';
import { renderContext } from './platform/renderContext';
import { InitializeAppUseCase } from './use-cases/initializeAppUseCase';
import { declineKeyFor } from './use-cases/payment/paymentCodeCatalog';
import { SubmitRejection } from './use-cases/types/SubmitRejection';
import { getCurrentHostName } from './utils/host';

const SUBMIT_REJECTION_KEYS: Record<SubmitRejection, TranslationKeys> = {
  [SubmitRejection.FirstNameRequired]: TranslationKeys.VALIDATION_FIRST_NAME_REQUIRED,
  [SubmitRejection.LastNameRequired]: TranslationKeys.VALIDATION_LAST_NAME_REQUIRED,
  [SubmitRejection.EmailRequired]: TranslationKeys.VALIDATION_EMAIL_REQUIRED,
  [SubmitRejection.PhoneRequired]: TranslationKeys.VALIDATION_PHONE_REQUIRED,
  [SubmitRejection.SsnRequired]: TranslationKeys.VALIDATION_SSN_REQUIRED,
  [SubmitRejection.DateOfBirthRequired]: TranslationKeys.VALIDATION_DATE_OF_BIRTH_REQUIRED,
  [SubmitRejection.ResidentialHistoryRequired]: TranslationKeys.VALIDATION_RESIDENTIAL_HISTORY_REQUIRED,
  [SubmitRejection.RequiredQuestionsNotAnswered]: TranslationKeys.VALIDATION_REQUIRED_QUESTIONS_NOT_ANSWERED,
  [SubmitRejection.RequiredFilesMissing]: TranslationKeys.VALIDATION_REQUIRED_FILES_MISSING,
  [SubmitRejection.TermsNotAccepted]: TranslationKeys.VALIDATION_TERMS_NOT_ACCEPTED,
  [SubmitRejection.AlreadySubmitted]: TranslationKeys.VALIDATION_ALREADY_SUBMITTED,
};

const ERROR_MESSAGE_KEYS: Array<[new (...args: never[]) => Error, TranslationKeys]> = [
  [CannotLoadPdfFileError, TranslationKeys.COULD_NOT_LOAD_PDF_FILE],
  [CannotRenderPdfPageError, TranslationKeys.COULD_NOT_LOAD_PDF_FILE],
  [CannotPerformScreeningError, TranslationKeys.CANNOT_PERFORM_SCREENING],
  [CannotMakePaymentsError, TranslationKeys.CANNOT_MAKE_PAYMENTS],
  [PaymentDeclinedError, TranslationKeys.PAYMENT_DECLINED],
  [RentalApplicationPaymentFailedError, TranslationKeys.PAYMENT_FAILED],
  [BusinessInfoNotFoundError, TranslationKeys.BUSINESS_INFO_NOT_FOUND],
  [RentalApplicationNotFoundError, TranslationKeys.RENTAL_APPLICATION_NOT_FOUND],
  [FileUploadFailedError, TranslationKeys.FILE_UPLOAD_FAILED],
  [FileNotAcceptedError, TranslationKeys.FILE_NOT_ACCEPTED],
  [CurrentUnitUnlistedError, TranslationKeys.CURRENT_UNIT_IS_UNLISTED],
  [LeaseInsuranceMissingFieldsError, TranslationKeys.LEASE_INSURANCE_MISSING_FIELDS],
  [LeaseOrSignatureNotFoundError, TranslationKeys.LEASE_OR_SIGNATURE_NOT_FOUND],
  [LandlordVerificationResponseRequiredError, TranslationKeys.LANDLORD_VERIFICATION_RESPONSE_REQUIRED],
  [InsuranceLinkNotFoundError, TranslationKeys.INSURANCE_LINK_NOT_FOUND],
  [FileSessionResponseInvalidError, TranslationKeys.FILE_SESSION_RESPONSE_INVALID],
  [CoApplicantInviteFailedError, TranslationKeys.CO_APPLICANTS_INVITE_FAILED],
  [StripeUnavailableError, TranslationKeys.STRIPE_COULD_NOT_BE_REACHED],
];

const declinedWithReason = (error?: Error): PaymentDeclinedError | undefined =>
  error instanceof PaymentDeclinedError && (error.failureCode || error.failureMessage) ? error : undefined;

export default function Root() {
  const { execute: initializeApp } = useUseCase(InitializeAppUseCase);

  const { t } = useLocalization();

  onMount(() => {
    void initializeApp(getCurrentHostName());
  });

  const NavigationHandler = () => {
    const navigate = useNavigate();
    return (
      <Navigator
        onNavigate={(url) => {
          if (isServer) {
            renderContext.redirect(url);
            return;
          }
          if (url.startsWith('http://') || url.startsWith('https://')) {
            window.location.href = url;
          } else {
            navigate(url);
          }
        }}
      />
    );
  };

  const getMessageForError = (error?: Error): string => {
    const match = ERROR_MESSAGE_KEYS.find(([cls]) => error instanceof cls);
    if (match) return t(match[1]);
    return t(TranslationKeys.SOMETHING_WENT_WRONG);
  };

  const getTitleForError = (error?: Error): string =>
    t(error instanceof ApplicationNotQualifiedError ? TranslationKeys.APPLICATION_NOT_QUALIFIED_TITLE : TranslationKeys.OOPS);

  const shouldReportError = (error: Error): boolean => {
    return ![FailToFetchError, UnauthorizedError, ValidationError].some((ErrorClass) => error instanceof ErrorClass);
  };

  return (
    <MetaProvider>
      <Router
        root={(props) => (
          <Suspense>
            <NavigationHandler />
            <CompanyStyling>
              <ErrorHandler
                onWillReportError={(error: Error) => {
                  if (shouldReportError(error)) {
                    Sentry.captureException(error);
                  }
                  return true;
                }}
                renderErrorDialog={({ error, onClose }) => {
                  return (
                    <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/10 p-4">
                      <div class="w-[500px] max-w-full overflow-hidden rounded-lg bg-white shadow-lg">
                        <div class="border-b border-gray-200 px-6 py-4">
                          <h2 class="text-primary text-center text-xl font-semibold">{getTitleForError(error)}</h2>
                        </div>
                        <div class="p-6 py-4">
                          <Show
                            when={error instanceof ApplicationNotQualifiedError}
                            fallback={
                              <>
                                <p class="text-center text-gray-700">{getMessageForError(error)}</p>
                                <Show when={declinedWithReason(error)} keyed>
                                  {(declined) => (
                                    <details class="mt-4 text-left text-sm text-gray-700">
                                      <summary class="mx-auto w-fit cursor-pointer list-inside text-primary">
                                        {t(TranslationKeys.VIEW_SPECIFIC_REASON)}
                                      </summary>
                                      <div class="mt-2 max-h-[40vh] overflow-y-auto">
                                        <p class="whitespace-pre-wrap break-words">
                                          {t(declineKeyFor(declined.failureCode) ?? TranslationKeys.PAYMENT_DECLINED)}
                                        </p>
                                        <Show when={declined.failureMessage} keyed>
                                          {(reason) => <p class="mt-2 whitespace-pre-wrap break-words text-gray-500">{reason}</p>}
                                        </Show>
                                      </div>
                                    </details>
                                  )}
                                </Show>
                              </>
                            }>
                            <p class="text-gray-700">{t(TranslationKeys.APPLICATION_NOT_QUALIFIED_BODY)}</p>
                            <ul class="mt-3 list-disc space-y-1 pl-6 text-gray-700">
                              <For each={(error as ApplicationNotQualifiedError).errors}>
                                {(code) => <li>{t(SUBMIT_REJECTION_KEYS[code])}</li>}
                              </For>
                            </ul>
                          </Show>
                        </div>
                        <div class="py-4 text-center">
                          <Button rounded="full" class="mx-auto font-bold" onClick={onClose}>
                            {t(TranslationKeys.CLOSE)}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                }}>
                <FileServiceProvider fileService={fileGateway.getFileService()}>
                  <StateTransfer />
                  <CustomMeta />
                  <HomeLayout>{props.children}</HomeLayout>
                  <ImagePreview />
                </FileServiceProvider>
              </ErrorHandler>
            </CompanyStyling>
          </Suspense>
        )}>
        <FileRoutes />
      </Router>
    </MetaProvider>
  );
}
